/**
 * Quality Inspection — Dashboard view.
 *
 * A single tab-page that summarises the QC world. Widgets:
 *   • KPI strip (5 cards)
 *   • Violations trend (stacked bar, weekly ≤90d / monthly above)
 *   • Top offending branches (horizontal stacked bars, top 8)
 *   • Top offending items (horizontal stacked bars, top 8)
 *   • Open vs Closed by severity (paired stacked bars)
 *   • Inspector activity (table)
 *
 * Filters: date range (preset + custom), branches (multi, permission-aware),
 * severity (multi). Server enforces every permission — the UI just narrows
 * what the caller can already see.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import {
    ResponsiveContainer,
    BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    Cell,
} from "recharts";
import { Loader2, Filter, RefreshCw, AlertTriangle, ClipboardCheck, TrendingUp, Timer, CheckCircle2, Users, Store, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { OPS } from "@/constants/testIds";

const SEV_COLORS = {
    critical: "#C1332F",   // brand red
    moderate: "#D1802F",   // brand orange
    minor:    "#B5B5B5",   // grey
};

const PRESETS = [
    { key: "7d",  days: 7 },
    { key: "30d", days: 30 },
    { key: "90d", days: 90, isDefault: true },
    { key: "ytd" },
    { key: "12m", days: 365 },
];

// -------------------------------------------------- helpers
function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}
function ytdISO() {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
}
function presetRange(key) {
    if (key === "ytd") return { start: ytdISO(), end: todayISO() };
    const p = PRESETS.find((x) => x.key === key);
    return { start: daysAgoISO(p?.days || 90), end: todayISO() };
}
function fmtCompactInt(n) {
    if (n == null) return "—";
    if (Math.abs(n) < 1000) return String(n);
    if (Math.abs(n) < 1e6) return `${(n / 1000).toFixed(1)}k`;
    return `${(n / 1e6).toFixed(1)}M`;
}
function fmtDaysOrDash(n) {
    if (n == null) return "—";
    return `${n} ${n === 1 ? "d" : "d"}`;
}

// -------------------------------------------------- filter popover pieces
function BranchFilter({ branches, selected, onChange, t, tn }) {
    const allSelected = selected.length === 0;
    const label = allSelected
        ? t("qc_dash_all_branches")
        : `${selected.length} ${t("qc_dash_branches")}`;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    data-testid={OPS.qcIns.dashboard.filterBranches}
                    className="h-9 gap-1.5"
                >
                    <Store className="h-4 w-4" />
                    <span className="truncate max-w-[130px]">{label}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-2">
                <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-xs text-[color:var(--ink-muted)]">
                        {selected.length}/{branches.length}
                    </span>
                    <button
                        type="button"
                        className="text-xs text-[color:var(--brand-orange)] hover:underline"
                        onClick={() => onChange([])}
                    >
                        {t("qc_dash_all_branches")}
                    </button>
                </div>
                <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-1">
                    {branches.map((b) => {
                        const on = selected.includes(b.id);
                        return (
                            <label key={b.id}
                                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[color:rgba(186,217,235,0.25)] cursor-pointer">
                                <Checkbox
                                    checked={on}
                                    onCheckedChange={(v) => {
                                        if (v) onChange([...new Set([...selected, b.id])]);
                                        else onChange(selected.filter((x) => x !== b.id));
                                    }}
                                />
                                <span className="text-xs truncate">{tn(b)}</span>
                            </label>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function SeverityFilter({ selected, onChange, t }) {
    const label = selected.length === 0 ? t("qc_dash_severity_all") : selected.map((s) => t(`qc_dash_sev_${s}`)).join(" · ");
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    data-testid={OPS.qcIns.dashboard.filterSeverity}
                    className="h-9 gap-1.5"
                >
                    <AlertTriangle className="h-4 w-4" />
                    <span className="truncate max-w-[150px]">{label}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-48 p-2">
                {["critical", "moderate", "minor"].map((s) => {
                    const on = selected.includes(s);
                    return (
                        <label key={s} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[color:rgba(186,217,235,0.25)] cursor-pointer">
                            <Checkbox
                                checked={on}
                                onCheckedChange={(v) => {
                                    if (v) onChange([...new Set([...selected, s])]);
                                    else onChange(selected.filter((x) => x !== s));
                                }}
                            />
                            <span className="text-xs">{t(`qc_dash_sev_${s}`)}</span>
                            <span className="ms-auto inline-block w-3 h-3 rounded-full" style={{ background: SEV_COLORS[s] }} />
                        </label>
                    );
                })}
            </PopoverContent>
        </Popover>
    );
}

function RangePresets({ presetKey, onPreset, t }) {
    // Preset key → i18n key mapping (kept explicit so translators can see all cases).
    const labelKey = (k) => {
        if (k === "12m") return "qc_dash_range_last_year";
        if (k === "ytd") return "qc_dash_range_ytd";
        return `qc_dash_range_last_${k}`;
    };
    return (
        <div className="flex items-center gap-1 flex-wrap" data-testid={OPS.qcIns.dashboard.filterRange}>
            {PRESETS.map((p) => (
                <Button
                    key={p.key}
                    type="button"
                    variant={presetKey === p.key ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-2 text-[11px] ${presetKey === p.key ? "bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white" : ""}`}
                    onClick={() => onPreset(p.key)}
                    data-testid={OPS.qcIns.dashboard.filterRangePreset(p.key)}
                >
                    {t(labelKey(p.key))}
                </Button>
            ))}
        </div>
    );
}

// -------------------------------------------------- KPI card
function Kpi({ testId, icon: Icon, label, value, tone, sub }) {
    const toneClass =
        tone === "danger" ? "text-[color:var(--danger)]" :
        tone === "warn" ? "text-[color:var(--brand-orange)]" :
        tone === "ok"    ? "text-[color:var(--success)]" :
                           "text-[color:var(--ink)]";
    return (
        <Card data-testid={testId} className="surface border-[color:var(--border-soft)] rounded-xl">
            <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[color:var(--ink-muted)]">
                    {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                    {label}
                </div>
                <div className={`mt-1 text-2xl sm:text-3xl font-extrabold tnum ${toneClass}`}>{value}</div>
                {sub ? <div className="text-[11px] text-[color:var(--ink-muted)] mt-0.5">{sub}</div> : null}
            </CardContent>
        </Card>
    );
}

// -------------------------------------------------- charts
function StackTooltip({ active, payload, label, t }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="rounded-md border border-[color:var(--border-soft)] bg-white p-2 text-xs shadow">
            <div className="font-semibold mb-1">{label}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                    <span className="text-[color:var(--ink-muted)]">{t(`qc_dash_sev_${p.dataKey}`)}</span>
                    <span className="ms-auto font-semibold">{p.value}</span>
                </div>
            ))}
        </div>
    );
}

function TrendChart({ trend, t }) {
    if (!trend || trend.length === 0 || trend.every((r) => r.total === 0)) {
        return <EmptyBlock t={t} />;
    }
    return (
        <div data-testid={OPS.qcIns.dashboard.trendChart} style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
                <BarChart data={trend} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip content={<StackTooltip t={t} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => t(`qc_dash_sev_${v}`)} />
                    <Bar dataKey="critical" stackId="s" fill={SEV_COLORS.critical} />
                    <Bar dataKey="moderate" stackId="s" fill={SEV_COLORS.moderate} />
                    <Bar dataKey="minor" stackId="s" fill={SEV_COLORS.minor} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function TopBarList({ rows, labelKey, testId, t, lang, secondaryLabelKey }) {
    if (!rows || rows.length === 0) return <EmptyBlock t={t} />;
    const max = Math.max(...rows.map((r) => r.total)) || 1;
    return (
        <div data-testid={testId} className="space-y-2">
            {rows.map((r, idx) => {
                const primary = r[labelKey] || r[`${labelKey}_en`] || r[`${labelKey}_ar`] || "";
                const critW = (r.critical / max) * 100;
                const modW  = (r.moderate / max) * 100;
                const minW  = (r.minor / max) * 100;
                return (
                    <div key={`${idx}-${primary}`} className="text-xs">
                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="truncate text-[color:var(--ink)]">
                                <bdi>{primary}</bdi>
                                {secondaryLabelKey && r[secondaryLabelKey] ? (
                                    <span className="text-[color:var(--ink-muted)] ms-1">— <bdi>{r[secondaryLabelKey]}</bdi></span>
                                ) : null}
                            </span>
                            <span className="ms-auto tnum font-semibold">{r.total}</span>
                        </div>
                        <div className="relative h-2.5 rounded-full bg-[color:rgba(224,222,217,0.4)] overflow-hidden flex" dir={lang === "ar" ? "rtl" : "ltr"}>
                            <div style={{ width: `${critW}%`, background: SEV_COLORS.critical }} />
                            <div style={{ width: `${modW}%`,  background: SEV_COLORS.moderate }} />
                            <div style={{ width: `${minW}%`,  background: SEV_COLORS.minor }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function OpenVsClosed({ data, t }) {
    // data: {open:{critical,moderate,minor}, closed:{...}}
    const rows = [
        { name: t("qc_dash_open"),   critical: data.open.critical,   moderate: data.open.moderate,   minor: data.open.minor,   kind: "open" },
        { name: t("qc_dash_closed"), critical: data.closed.critical, moderate: data.closed.moderate, minor: data.closed.minor, kind: "closed" },
    ];
    const totalAny = rows.some((r) => r.critical + r.moderate + r.minor > 0);
    if (!totalAny) return <EmptyBlock t={t} />;
    return (
        <div data-testid={OPS.qcIns.dashboard.openVsClosed} style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip content={<StackTooltip t={t} />} />
                    <Bar dataKey="critical" stackId="s" fill={SEV_COLORS.critical} />
                    <Bar dataKey="moderate" stackId="s" fill={SEV_COLORS.moderate} />
                    <Bar dataKey="minor"    stackId="s" fill={SEV_COLORS.minor} radius={[4, 4, 0, 0]}>
                        {rows.map((r, i) => <Cell key={i} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function InspectorTable({ rows, t }) {
    if (!rows || rows.length === 0) return <EmptyBlock t={t} />;
    return (
        <div data-testid={OPS.qcIns.dashboard.inspectorTable} className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-[color:var(--ink-muted)] text-[10px] uppercase tracking-wider">
                        <th className="text-start py-1.5 pe-2">{t("qc_dash_col_inspector")}</th>
                        <th className="text-end py-1.5 px-2">{t("qc_dash_col_rounds")}</th>
                        <th className="text-end py-1.5 px-2">{t("qc_dash_col_total_violations")}</th>
                        <th className="text-end py-1.5 px-2">{t("qc_dash_col_avg_violations")}</th>
                        <th className="text-end py-1.5 ps-2">{t("qc_dash_col_last_submission")}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.user_id} className="border-t border-[color:var(--border-soft)]/60">
                            <td className="py-2 pe-2 font-medium text-[color:var(--ink)] truncate max-w-[180px]"><bdi>{r.name || r.user_id}</bdi></td>
                            <td className="py-2 px-2 text-end tnum">{r.rounds}</td>
                            <td className="py-2 px-2 text-end tnum">{r.violations_total}</td>
                            <td className="py-2 px-2 text-end tnum">{r.avg_violations.toFixed(2)}</td>
                            <td className="py-2 ps-2 text-end tnum text-[color:var(--ink-muted)]" dir="ltr">
                                {r.last_submission ? r.last_submission.slice(0, 10) : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function EmptyBlock({ t }) {
    return (
        <div className="py-6 text-center text-xs text-[color:var(--ink-muted)]">{t("qc_dash_no_data")}</div>
    );
}

// -------------------------------------------------- Cross-module branch performance
function fmtSAR(v) {
    if (v == null) return "—";
    if (Math.abs(v) < 1000) return v.toFixed(0);
    if (Math.abs(v) < 1e6) return `${(v / 1000).toFixed(1)}k`;
    return `${(v / 1e6).toFixed(2)}M`;
}

function BranchPerformanceTable({ rows, t, lang }) {
    if (!rows || rows.length === 0) return <EmptyBlock t={t} />;
    return (
        <div data-testid={OPS.qcIns.dashboard.branchPerf} className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-[color:var(--ink-muted)] text-[10px] uppercase tracking-wider">
                        <th className="text-start py-1.5 pe-2">{t("qc_dash_col_branch")}</th>
                        <th className="text-end py-1.5 px-2">{t("qc_dash_col_violations")}</th>
                        <th className="text-end py-1.5 px-2">{t("qc_dash_col_sales_net")}</th>
                        <th className="text-end py-1.5 px-2">{t("qc_dash_col_pnl_net")}</th>
                        <th className="text-end py-1.5 ps-2">{t("qc_dash_col_flags")}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const label = (lang === "ar" ? r.branch_name_ar : r.branch_name_en) || r.branch_name_ar || r.branch_id;
                        const secondary = lang === "ar" ? r.branch_name_en : r.branch_name_ar;
                        return (
                            <tr key={r.branch_id} data-testid={OPS.qcIns.dashboard.branchPerfRow(r.branch_id)} className="border-t border-[color:var(--border-soft)]/60">
                                <td className="py-2 pe-2 max-w-[220px]">
                                    <div className="font-medium text-[color:var(--ink)] truncate"><bdi>{label}</bdi></div>
                                    {secondary && secondary !== label ? (
                                        <div className="text-[10px] text-[color:var(--ink-muted)] truncate"><bdi>{secondary}</bdi></div>
                                    ) : null}
                                </td>
                                <td className="py-2 px-2 text-end tnum">
                                    {r.violations_total > 0 ? (
                                        <div className="inline-flex items-center gap-1">
                                            {r.critical > 0 ? <span title={t("qc_dash_critical")} style={{ color: SEV_COLORS.critical }} className="font-semibold">{r.critical}</span> : null}
                                            {r.moderate > 0 ? <span title={t("qc_dash_moderate")} style={{ color: SEV_COLORS.moderate }} className="font-semibold">{r.critical > 0 ? "·" : ""}{r.moderate}</span> : null}
                                            {r.minor > 0 ? <span title={t("qc_dash_minor")} style={{ color: SEV_COLORS.minor }} className="font-semibold">{r.critical > 0 || r.moderate > 0 ? "·" : ""}{r.minor}</span> : null}
                                            <span className="ms-1.5 text-[color:var(--ink)] font-bold">{r.violations_total}</span>
                                        </div>
                                    ) : <span className="text-[color:var(--ink-muted)]">0</span>}
                                </td>
                                <td className="py-2 px-2 text-end tnum text-[color:var(--ink)]" dir="ltr">{fmtSAR(r.sales_net)}</td>
                                <td className="py-2 px-2 text-end tnum" dir="ltr">
                                    <span style={{ color: r.pnl_net == null ? "var(--ink-muted)" : r.pnl_net >= 0 ? "var(--success)" : "var(--danger)" }}>
                                        {fmtSAR(r.pnl_net)}
                                    </span>
                                </td>
                                <td className="py-2 ps-2 text-end">
                                    {r.finance_only ? (
                                        <Badge variant="secondary" className="text-[10px] gap-1 bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0">
                                            {t("branch_finance_only")}
                                        </Badge>
                                    ) : null}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// -------------------------------------------------- main
export default function QCDashboardView() {
    const { me } = useAuth();
    const { t, tn, lang } = useI18n();
    const branches = me?.sales?.branches || [];
    const [presetKey, setPresetKey] = useState("90d");
    const [dates, setDates] = useState(presetRange("90d"));
    const [customStart, setCustomStart] = useState(dates.start);
    const [customEnd, setCustomEnd] = useState(dates.end);
    const [branchIds, setBranchIds] = useState([]);
    const [severities, setSeverities] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const applyPreset = useCallback((k) => {
        setPresetKey(k);
        const r = presetRange(k);
        setDates(r);
        setCustomStart(r.start);
        setCustomEnd(r.end);
    }, []);

    const applyCustom = useCallback(() => {
        if (!customStart || !customEnd) return;
        if (customStart > customEnd) {
            toast.error(t("qc_dash_from") + " ≤ " + t("qc_dash_to"));
            return;
        }
        setPresetKey("custom");
        setDates({ start: customStart, end: customEnd });
    }, [customStart, customEnd, t]);

    const clearFilters = useCallback(() => {
        applyPreset("90d");
        setBranchIds([]);
        setSeverities([]);
    }, [applyPreset]);

    // fetch
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = { start: dates.start, end: dates.end };
                if (branchIds.length) params.branch_ids = branchIds.join(",");
                if (severities.length) params.severities = severities.join(",");
                const r = await api.get("/qc-inspection/dashboard", { params });
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail || t("error_generic"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [dates, branchIds, severities, t]);

    const totalActive = useMemo(() => {
        if (!data) return 0;
        return data.kpis.rounds_submitted + data.kpis.violations_opened;
    }, [data]);

    return (
        <div data-testid={OPS.qcIns.dashboard.page} className="space-y-5">
            {/* --------- header --------- */}
            <div className="flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0">
                    <h2 className="text-lg font-bold text-[color:var(--ink)] flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        {t("qc_dash_title")}
                    </h2>
                    <p className="text-xs text-[color:var(--ink-muted)] mt-0.5">{t("qc_dash_subtitle")}</p>
                </div>
                <div className="flex items-center gap-1.5">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin text-[color:var(--ink-muted)]" /> : null}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-[11px] gap-1"
                    >
                        <RefreshCw className="h-3 w-3" />
                        {t("qc_dash_clear_filters")}
                    </Button>
                </div>
            </div>

            {/* --------- filter bar --------- */}
            <Card data-testid={OPS.qcIns.dashboard.filterBar} className="surface border-[color:var(--border-soft)]">
                <CardContent className="p-3 space-y-3">
                    {/* Row 1: presets + custom date range + apply */}
                    <div className="flex flex-wrap items-center gap-2">
                        <RangePresets presetKey={presetKey} onPreset={applyPreset} t={t} />
                        <div className="flex flex-wrap items-center gap-1 lg:ms-auto">
                            <Input
                                type="date"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                className="h-9 w-[140px] text-xs"
                                dir="ltr"
                            />
                            <span className="text-xs text-[color:var(--ink-muted)]">→</span>
                            <Input
                                type="date"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                className="h-9 w-[140px] text-xs"
                                dir="ltr"
                            />
                            <Button variant="soft" size="sm" onClick={applyCustom} className="gap-1">
                                <Filter className="h-3 w-3" /> {t("qc_dash_apply")}
                            </Button>
                        </div>
                    </div>
                    {/* Row 2: chip filters + current-range badge */}
                    <div className="flex flex-wrap items-center gap-2">
                        <BranchFilter branches={branches} selected={branchIds} onChange={setBranchIds} t={t} tn={tn} />
                        <SeverityFilter selected={severities} onChange={setSeverities} t={t} />
                        {data ? (
                            <Badge variant="secondary" className="ms-auto font-normal">
                                <span dir="ltr">{data.range.start} → {data.range.end}</span>
                                <span className="mx-1.5">·</span>
                                {data.range.bucket === "week" ? "weekly" : "monthly"}
                            </Badge>
                        ) : null}
                    </div>
                </CardContent>
            </Card>

            {/* --------- KPI strip --------- */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Kpi testId={OPS.qcIns.dashboard.kpiRounds} icon={ClipboardCheck}
                     label={t("qc_dash_kpi_rounds")} value={fmtCompactInt(data?.kpis.rounds_submitted)} />
                <Kpi testId={OPS.qcIns.dashboard.kpiViolations} icon={AlertTriangle}
                     label={t("qc_dash_kpi_violations")} value={fmtCompactInt(data?.kpis.violations_opened)}
                     tone={data && data.kpis.violations_opened > 0 ? "warn" : undefined} />
                <Kpi testId={OPS.qcIns.dashboard.kpiClosedPct} icon={CheckCircle2}
                     label={t("qc_dash_kpi_closed_pct")}
                     value={data ? `${data.kpis.closed_pct.toFixed(0)}%` : "—"}
                     tone={data && data.kpis.closed_pct >= 80 ? "ok" : "warn"} />
                <Kpi testId={OPS.qcIns.dashboard.kpiOverdue} icon={Timer}
                     label={t("qc_dash_kpi_overdue")}
                     value={fmtCompactInt(data?.kpis.overdue_count)}
                     tone={data && data.kpis.overdue_count > 0 ? "danger" : undefined} />
                <Kpi testId={OPS.qcIns.dashboard.kpiAvgTtc} icon={Timer}
                     label={t("qc_dash_kpi_avg_ttc")}
                     sub={t("qc_dash_kpi_avg_ttc_note")}
                     value={
                         data ? (
                             <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0 text-lg">
                                 <span style={{ color: SEV_COLORS.critical }}>{fmtDaysOrDash(data.kpis.avg_ttc_days.critical)}</span>
                                 <span className="text-[10px] text-[color:var(--ink-muted)]">/</span>
                                 <span style={{ color: SEV_COLORS.moderate }}>{fmtDaysOrDash(data.kpis.avg_ttc_days.moderate)}</span>
                                 <span className="text-[10px] text-[color:var(--ink-muted)]">/</span>
                                 <span style={{ color: SEV_COLORS.minor }}>{fmtDaysOrDash(data.kpis.avg_ttc_days.minor)}</span>
                             </span>
                         ) : "—"
                     } />
            </div>

            {/* --------- charts row 1: Trend --------- */}
            <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                <CardHeader className="pb-2">
                    <div className="flex items-baseline justify-between gap-2">
                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("qc_dash_trend_title")}</h3>
                        <span className="text-[11px] text-[color:var(--ink-muted)]">{t("qc_dash_trend_desc")}</span>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    {data ? <TrendChart trend={data.trend} t={t} /> : <SkeletonBlock />}
                </CardContent>
            </Card>

            {/* --------- charts row 2: Top branches + Open vs Closed --------- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                    <CardHeader className="pb-2">
                        <h3 className="text-sm font-bold text-[color:var(--ink)] flex items-center gap-1.5">
                            <Store className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            {t("qc_dash_top_branches_title")}
                        </h3>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {data ? (
                            <TopBarList
                                rows={data.top_branches.map((r) => ({ ...r, label: (lang === "ar" ? r.branch_name_ar : r.branch_name_en) || r.branch_name_ar }))}
                                labelKey="label"
                                testId={OPS.qcIns.dashboard.topBranches}
                                t={t}
                                lang={lang}
                            />
                        ) : <SkeletonBlock />}
                    </CardContent>
                </Card>
                <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                    <CardHeader className="pb-2">
                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("qc_dash_open_vs_closed_title")}</h3>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {data ? <OpenVsClosed data={data.open_vs_closed} t={t} /> : <SkeletonBlock />}
                    </CardContent>
                </Card>
            </div>

            {/* --------- charts row 3: Top items + Inspector table --------- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                    <CardHeader className="pb-2">
                        <h3 className="text-sm font-bold text-[color:var(--ink)] flex items-center gap-1.5">
                            <Tag className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            {t("qc_dash_top_items_title")}
                        </h3>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {data ? (
                            <TopBarList
                                rows={data.top_items.map((r) => ({ ...r, label: r.item, secondary: lang === "ar" ? r.section_title_ar : r.section_title_en }))}
                                labelKey="label"
                                secondaryLabelKey="secondary"
                                testId={OPS.qcIns.dashboard.topItems}
                                t={t}
                                lang={lang}
                            />
                        ) : <SkeletonBlock />}
                    </CardContent>
                </Card>
                <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                    <CardHeader className="pb-2">
                        <h3 className="text-sm font-bold text-[color:var(--ink)] flex items-center gap-1.5">
                            <Users className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            {t("qc_dash_inspector_title")}
                        </h3>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {data ? <InspectorTable rows={data.inspector_activity} t={t} /> : <SkeletonBlock />}
                    </CardContent>
                </Card>
            </div>

            {/* --------- charts row 4: Cross-module branch performance --------- */}
            {data?.branch_perf_enabled ? (
            <Card className="surface border-[color:var(--border-soft)] rounded-xl">
                <CardHeader className="pb-2">
                    <div className="flex items-baseline justify-between gap-2">
                        <h3 className="text-sm font-bold text-[color:var(--ink)] flex items-center gap-1.5">
                            <Store className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            {t("qc_dash_branch_perf_title")}
                        </h3>
                        <span className="text-[11px] text-[color:var(--ink-muted)]">{t("qc_dash_branch_perf_desc")}</span>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    {data ? <BranchPerformanceTable rows={data.branch_performance || []} t={t} lang={lang} /> : <SkeletonBlock />}
                </CardContent>
            </Card>
            ) : null}

            {/* --------- empty helper --------- */}
            {data && totalActive === 0 ? (
                <div data-testid={OPS.qcIns.dashboard.empty} className="text-center py-4 text-xs text-[color:var(--ink-muted)]">
                    {t("qc_dash_no_data")}
                </div>
            ) : null}
        </div>
    );
}

function SkeletonBlock() {
    return <div className="animate-pulse h-40 rounded-lg bg-[color:rgba(224,222,217,0.35)]" />;
}
